/* eslint-disable new-cap */
import React, { useContext } from 'react';
import I18n from 'i18n-js';
import { FetchResult } from '@apollo/client';
import { Redirect } from '@reverbdotcom/commons/src/routing_result';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import {
  core_apimessages_ListingStateUpdate,
} from '@reverbdotcom/commons/src/rql';
import { CheckCircleIcon } from '@reverbdotcom/cadence/icons/react';
import SellerListingCardContext from '../SellerListingCardContext';
import { ActionType } from '../seller_listing_card_reducer';
import { Paths } from '../../shared/url_helpers';

interface IMutationError {
  extensions: {
    errors: [{
      message: string
    }]
  },
}

type IExtendedMutationResult<TData> = FetchResult<TData> & {
  errors?: IMutationError[]
};

// Follows statuses used in backend reverb/app/reverb/shops/prepublish_status.rb
export enum PrePublishStatus {
  PAYMENT_POLICY_REQUIRED = 'payment_policy_required',
  SELLER_VERIFICATION_REQUIRED = 'seller_verification_required',
  PROGRESSIVE_ONBOARDING_REQUIRED = 'progressive_onboarding_required',
}

function ListingRowPublishButton({ updateListingState }) {
  const { cardState, dispatch, listingQuery } = useContext(
    SellerListingCardContext,
  );

  const [queryListing] = listingQuery;

  async function startPublish() {
    dispatch({
      type: ActionType.SET_LOADING_LISTING,
      payload: {
        loadingListing: true,
      },
    });

    try {
      const res = await updateListingState({
        variables: {
          input: {
            productId: cardState.listing.id,
            newState: core_apimessages_ListingStateUpdate.LIVE,
          },
        },
      }) as IExtendedMutationResult<typeof updateListingState>;

      const { hasErrors } = checkResponse(res);

      if (hasErrors) {
        const [userError, nextError] = formatErrors(res.errors);
        if (nextError) {
          handleRedirectErrors(nextError.message);
        } else {
          dispatch({
            type: ActionType.SET_LOADING_LISTING,
            payload: {
              loadingListing: false,
            },
          });
          dispatch({
            type: ActionType.SHOW_TOAST,
            payload: {
              isSuccess: false,
              message: userError?.message,
            },
          });
        }
      } else {
        refetchListing();
      }
    } catch (error) {
      dispatch({
        type: ActionType.SET_LOADING_LISTING,
        payload: {
          loadingListing: false,
        },
      });
      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: false,
          message: I18n.t(
            'discovery.listingsManagement.genericError',
          ),
        },
      });
    }
  }

  async function refetchListing() {
    try {
      const res = await queryListing({
        variables: {
          id: cardState.listing.id,
        },
      });

      const { hasErrors, hasData } = checkResponse(res);

      if (hasErrors || !hasData) {
        throw new Error();
      }

      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: true,
          message: I18n.t(
            'discovery.listingsManagement.sellerListingCard.publishSuccess',
          ),
        },
      });

      dispatch({
        type: ActionType.SET_LISTING,
        payload: {
          listing: res.data.listing,
        },
      });
    } catch (error) {
      dispatch({
        type: ActionType.SHOW_TOAST,
        payload: {
          isSuccess: false,
          message: I18n.t(
            'discovery.listingsManagement.sellerListingCard.listingFetchFail',
          ),
        },
      });
    }

    dispatch({
      type: ActionType.SET_LOADING_LISTING,
      payload: {
        loadingListing: false,
      },
    });
  }

  function checkResponse(
    res,
  ) {
    return {
      hasErrors: !!res.errors?.length,
      hasData: !!res.data,
    };
  }

  function formatErrors(errors) {
    return errors[0]?.extensions?.errors || [];
  }

  function handleRedirectErrors(errorMessage: string) {

    let url: string;
    switch (errorMessage) {
      case PrePublishStatus.PAYMENT_POLICY_REQUIRED:
        url = Paths.getPaid.expand({
          listingId: cardState.listing.id,
          query: { react_sell_form_publish: true },
        });
        break;
      case PrePublishStatus.SELLER_VERIFICATION_REQUIRED:
        url = Paths.sellerVerificationOnboarding.expand({
          query: {
            listing_id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
        break;
      case PrePublishStatus.PROGRESSIVE_ONBOARDING_REQUIRED:
        url = Paths.shopOnboardingSetup.expand({
          query: {
            listing_id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
        break;
      default:
        // If it's not an error related to the prepublish status, redirect to the SellFormPostPublish to display error messages
        url = Paths.listingSubmit.expand({
          query: {
            id: cardState.listing.id,
            react_sell_form_publish: true,
          },
        });
    }
    Redirect({ url, fullPageReload: true });
  }

  return (
    <>
      <RCButton
        onClick={startPublish}
        variant="primary"
        disabled={!cardState.listing.publishValidation?.isValid}
        fullWidth
      >
        <RCTextWithIcon svgComponent={CheckCircleIcon} placement="right">
          {I18n.t('discovery.listingsManagement.sellerListingCard.publish')}
        </RCTextWithIcon>
      </RCButton>
    </>
  );
}

export default ListingRowPublishButton;
